<template>
  <Callout.Container edge="bottom">
    <Callout.Root v-if="IS_IMPERSONATED" variant="warning" tone="alert">
      Logged in as this user.

      <template #actions>
        <Callout.Action href="/admin/logout/">Return to admin</Callout.Action>
      </template>
    </Callout.Root>

    <Callout.Root
      v-if="
        INCIDENT_MESSAGE_VALUE &&
        INCIDENT_MESSAGE_VALUE !== 'INCIDENT_MESSAGE' &&
        account.flags.includes('should_see_incident')
      "
      variant="error"
      tone="alert"
    >
      {{ INCIDENT_MESSAGE_VALUE }}

      <template #actions>
        <Callout.Action href="https://status.buttondown.com">
          Status page
        </Callout.Action>
      </template>
    </Callout.Root>

    <BlockerBanner />

    <Callout.Root
      v-if="(disabledWebhookCount ?? 0) > 0 && route.name !== '/webhooks/[id]'"
      variant="warning"
      tone="alert"
    >
      <template v-if="disabledWebhookCount === 1">
        A webhook is disabled — no events are being delivered.
      </template>
      <template v-else>
        {{ disabledWebhookCount }} webhooks are disabled — no events are being
        delivered.
      </template>

      <template #actions>
        <Callout.Action :to="{ name: '/webhooks' }"
          >View webhooks</Callout.Action
        >
      </template>
    </Callout.Root>
  </Callout.Container>

  <TooltipProvider :delay-duration="1000" ignore-non-keyboard-focus>
    <DrawerPortal.Root
      :key="newsletter.id"
      v-slot="{ open: isDrawerOpen, variant: drawerVariant }"
    >
      <div
        id="app-inner"
        class="relative z-0 flex h-dvh min-w-0 overflow-hidden bg-white transition dark:bg-background dark:text-white"
        :style="{ '--tint-color': newsletter.tint_color || '#0069FF' }"
      >
        <Aside @search="isSearchOpen = true" />

        <div
          class="z-0 flex min-w-0 grow flex-col transition-all duration-300 ease-bounce @container/panel motion-reduce:transition-none"
          :inert="isMobileSidebarOpen || (isDrawerOpen && isMobile)"
          :class="[isWidescreen && isDesktopSidebarOpen && 'ml-52']"
          :style="panelOffsetStyle(isDrawerOpen, drawerVariant)"
        >
          <RouterView />
        </div>

        <DrawerRenderer />
      </div>
    </DrawerPortal.Root>

    <EmphasisOverlay />

    <BulkActionProvider />

    <ErrorModal v-for="error in errors" :key="error.detail" :error="error" />

    <SearchModal v-if="isSearchOpen" @close="isSearchOpen = false" />
    <ShortcutRenderer />
    <ToastRenderer />
  </TooltipProvider>
</template>

<script setup lang="ts">
import { TooltipProvider } from "reka-ui";
import { type CSSProperties, computed, ref, toRef, watch } from "vue";
import { useRoute } from "vue-router";

import BulkActionProvider from "@/components/bulk-actions/BulkActionProvider.vue";
import Callout from "@/design_system/Callout";
import {
  drawerOffsetStyle,
  useReservesDrawerSpace,
} from "@/design_system/Drawer/layout";
import * as DrawerPortal from "@/design_system/Drawer/Portal";
import { useWebhooksListingQuery } from "@/lib/api/queries/webhooks/list";
import { useMediaQuery } from "@/lib/composables/media-query";
import { IS_IMPERSONATED } from "@/lib/environment";
import { useKeybinds } from "@/lib/hotkeys";
import { useAccount, useNewsletter } from "@/store/stores/account";
import { useStore as useErrorsStore } from "@/store/stores/errors";
import { useStore as useUiStatesStore } from "@/store/stores/ui-states";

import { Aside } from "./Aside";
import BlockerBanner from "./BlockerBanner.vue";
import DrawerRenderer from "./DrawerRenderer.vue";
import EmphasisOverlay from "./EmphasisOverlay.vue";
import ErrorModal from "./ErrorModal.vue";
import SearchModal from "./SearchModal/Component.vue";
import ShortcutRenderer from "./ShortcutRenderer.vue";
import ToastRenderer from "./ToastRenderer.vue";

window.document.title = "Buttondown";

// Expose INCIDENT_MESSAGE to template
// Read from window.INCIDENT_MESSAGE (set by HTML script tag or test setup)
// Vite's define will replace this at compile time if INCIDENT_MESSAGE is defined
const INCIDENT_MESSAGE_VALUE =
  typeof window !== "undefined" ? (window as any).INCIDENT_MESSAGE || "" : "";

const route = useRoute();

const errorsStore = useErrorsStore();
const uiStates = useUiStatesStore();

const hideSidebarParam = computed(() => {
  const hideSidebar = route.query.hideSidebar;
  return hideSidebar === "true" || hideSidebar === "1";
});

const isDesktopSidebarOpen = computed(() => {
  return !hideSidebarParam.value && uiStates.isDesktopSidebarOpen;
});

const isMobileSidebarOpen = computed(() => {
  return !hideSidebarParam.value && uiStates.isMobileSidebarOpen;
});

const account = useAccount();
const newsletter = useNewsletter();

const hasWebhooksFeature = computed(
  () => newsletter.value.enabled_features?.includes("webhooks") ?? false
);

const { count: disabledWebhookCount } = useWebhooksListingQuery({
  enabled: hasWebhooksFeature,
  status: "disabled",
});

const errors = toRef(errorsStore, "resource");

const isMobile = useMediaQuery("(width < 640px)");

const isWidescreen = useMediaQuery("(width >= 1024px)");
const reservesDrawerSpace = useReservesDrawerSpace();

const panelOffsetStyle = (
  isDrawerOpen: boolean,
  drawerVariant: DrawerPortal.DrawerVariant
): CSSProperties => {
  if (!reservesDrawerSpace.value || !isDrawerOpen) {
    return { marginRight: "0px" };
  }
  return drawerOffsetStyle(drawerVariant);
};

const isSearchOpen = ref(false);

watch(
  toRef(uiStates, "appThemeNormalized"),
  (theme) => {
    document.documentElement.classList.toggle(
      "dark",
      theme === "dark" ||
        (window.location.search.includes("dark=true") &&
          !window.location.search.includes("dark=false"))
    );
  },
  { immediate: true }
);

watch([isWidescreen, () => route.path], () => {
  uiStates.isMobileSidebarOpen = false;
});

useKeybinds({
  "$mod+k": {
    name: `Open quick search`,
    group: `Application`,
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      isSearchOpen.value = true;
      return true;
    },
  },
  "[": {
    name: `Toggle sidebar`,
    group: `Application`,
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      if (isWidescreen.value) {
        uiStates.isDesktopSidebarOpen = !uiStates.isDesktopSidebarOpen;
      } else {
        uiStates.isMobileSidebarOpen = !uiStates.isMobileSidebarOpen;
      }

      return true;
    },
  },
  Escape: {
    name: `Hide sidebar`,
    group: `Application`,
    hidden: true,
    run(ev) {
      if (ev.defaultPrevented) {
        return;
      }

      if (isMobileSidebarOpen.value && !isWidescreen.value) {
        uiStates.isMobileSidebarOpen = false;
        return true;
      }
    },
  },
});
</script>

<style lang="css">
@import "@/styles/base.css";
</style>
